import { useState } from "react";
import { Plus, Send, Save, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// La tasa fija de repostaje del contrato (POLICIES.fuel_penalty_eur); editable
// en la línea por si algún día cambia el contrato antes que este valor.
const REFUEL_FEE_EUR = 50;

const KIND_LABELS = {
  repair: "Reparación",
  km: "Km adicionales",
  fuel: "Combustible",
  refuel_fee: "Tasa repostaje",
  other: "Otro",
};

export default function ExpenseSheet({ reservation, onSaved }) {
  const sheet = reservation.expense_sheet || null;
  const co = reservation.checkout_data || {};
  const [items, setItems] = useState(sheet?.items?.map((i) => ({ ...i })) || []);
  const [notes, setNotes] = useState(sheet?.notes || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const setItem = (idx, k, v) => {
    setItems((arr) =>
      arr.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [k]: v };
        // Litros/km × precio unitario → importe en vivo
        if ((k === "qty" || k === "unit_price") && next.qty !== "" && next.unit_price !== "") {
          const q = Number(next.qty);
          const u = Number(next.unit_price);
          if (!Number.isNaN(q) && !Number.isNaN(u)) next.amount = Math.round(q * u * 100) / 100;
        }
        return next;
      })
    );
  };

  const addRepair = () =>
    setItems((a) => [...a, { kind: "repair", concept: "Reparación de daños: ", qty: null, unit_price: null, amount: "" }]);
  const addKm = () => {
    const kmExtra = Number(co.km_extra || 0);
    const rate = Number(reservation.extra_km_rate || 0.2);
    setItems((a) => [
      ...a,
      {
        kind: "km",
        concept: kmExtra > 0 ? `Kilómetros adicionales (${kmExtra} km × ${rate.toFixed(2)} €)` : "Kilómetros adicionales",
        qty: kmExtra > 0 ? kmExtra : "",
        unit_price: rate,
        amount: kmExtra > 0 ? Math.round(kmExtra * rate * 100) / 100 : "",
      },
    ]);
  };
  const addFuel = () =>
    setItems((a) => [...a, { kind: "fuel", concept: "Combustible faltante", qty: "", unit_price: "", amount: "" }]);
  const addRefuelFee = () =>
    setItems((a) => [...a, { kind: "refuel_fee", concept: "Tasa administrativa de repostaje", qty: null, unit_price: null, amount: REFUEL_FEE_EUR }]);
  const addOther = () =>
    setItems((a) => [...a, { kind: "other", concept: "", qty: null, unit_price: null, amount: "" }]);

  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const cleanItems = () =>
    items
      .filter((i) => (i.concept || "").trim())
      .map((i) => ({
        kind: i.kind,
        concept: i.concept.trim(),
        qty: i.qty === "" || i.qty == null ? null : Number(i.qty),
        unit_price: i.unit_price === "" || i.unit_price == null ? null : Number(i.unit_price),
        amount: Number(i.amount) || 0,
      }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/reservations/${reservation.id}/expenses`, { items: cleanItems(), notes });
      toast.success("Presupuesto guardado");
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!items.length) return toast.error("Añade al menos una línea");
    if (items.some((i) => !(Number(i.amount) > 0)))
      return toast.error("Hay líneas sin importe; complétalas o elimínalas");
    if (!window.confirm(`Se enviará el presupuesto (${total.toFixed(2)} €) por email a ${reservation.client_email}. ¿Continuar?`))
      return;
    setSending(true);
    try {
      await api.put(`/reservations/${reservation.id}/expenses`, { items: cleanItems(), notes });
      await api.post(`/reservations/${reservation.id}/expenses/send`);
      toast.success("Presupuesto enviado al viajero");
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="expense-sheet">
      {sheet?.status === "sent" && (
        <div className="bg-ok/10 border border-ok/30 rounded-xl px-4 py-3 text-sm text-forest">
          Enviado al viajero {sheet.sent_count > 1 ? `${sheet.sent_count} veces, último` : ""} el{" "}
          {new Date(sheet.sent_at).toLocaleString("es-ES")}. Puedes editarlo y reenviarlo.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={addRepair} data-testid="exp-add-repair"><Plus className="h-4 w-4 mr-1" />Reparación</Button>
        <Button variant="outline" size="sm" onClick={addKm} data-testid="exp-add-km"><Plus className="h-4 w-4 mr-1" />Km adicionales</Button>
        <Button variant="outline" size="sm" onClick={addFuel} data-testid="exp-add-fuel"><Plus className="h-4 w-4 mr-1" />Combustible</Button>
        <Button variant="outline" size="sm" onClick={addRefuelFee} data-testid="exp-add-fee"><Plus className="h-4 w-4 mr-1" />Tasa repostaje ({REFUEL_FEE_EUR} €)</Button>
        <Button variant="outline" size="sm" onClick={addOther} data-testid="exp-add-other"><Plus className="h-4 w-4 mr-1" />Otro</Button>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-warmgray">
          Sin líneas todavía. Añade el presupuesto de reparación, los km adicionales, el
          combustible faltante o la tasa de repostaje con los botones de arriba.
        </p>
      )}

      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="bg-white border border-sand rounded-xl p-3 flex flex-wrap items-end gap-2" data-testid={`exp-item-${i}`}>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-warmgray-light w-full sm:w-24 shrink-0 pb-2">
              {KIND_LABELS[it.kind] || it.kind}
            </span>
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Concepto</Label>
              <Input value={it.concept} onChange={(e) => setItem(i, "concept", e.target.value)} data-testid={`exp-concept-${i}`} />
            </div>
            {(it.kind === "fuel" || it.kind === "km") && (
              <>
                <div className="w-24">
                  <Label className="text-xs">{it.kind === "fuel" ? "Litros" : "Km"}</Label>
                  <Input type="number" min="0" step="0.1" value={it.qty ?? ""} onChange={(e) => setItem(i, "qty", e.target.value)} />
                </div>
                <div className="w-24">
                  <Label className="text-xs">€/{it.kind === "fuel" ? "litro" : "km"}</Label>
                  <Input type="number" min="0" step="0.01" value={it.unit_price ?? ""} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                </div>
              </>
            )}
            <div className="w-28">
              <Label className="text-xs">Importe €</Label>
              <Input type="number" min="0" step="0.01" value={it.amount} onChange={(e) => setItem(i, "amount", e.target.value)} data-testid={`exp-amount-${i}`} />
            </div>
            <Button variant="ghost" size="icon" className="text-terracotta" onClick={() => setItems((a) => a.filter((_, j) => j !== i))} aria-label="Eliminar línea">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between bg-ivory border border-sand rounded-xl px-4 py-3">
        <span className="text-sm text-warmgray">
          Fianza: {Number(reservation.deposit_amount || 0).toFixed(2)} €
          {total > Number(reservation.deposit_amount || 0) && (
            <span className="text-terracotta font-semibold"> · el exceso se cobrará a la tarjeta guardada</span>
          )}
        </span>
        <span className="font-display text-lg font-bold text-charcoal" data-testid="exp-total">
          Total: {total.toFixed(2)} €
        </span>
      </div>

      <div>
        <Label>Notas internas (no se envían)</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="exp-notes" />
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={save} disabled={saving || sending} data-testid="exp-save">
          <Save className="h-4 w-4 mr-2" />{saving ? "Guardando..." : "Guardar borrador"}
        </Button>
        <Button onClick={send} disabled={saving || sending} className="bg-forest-deep hover:bg-forest text-white" data-testid="exp-send">
          <Send className="h-4 w-4 mr-2" />{sending ? "Enviando..." : "Enviar al viajero"}
        </Button>
      </div>
    </div>
  );
}
